import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { BLOG_TITLE, BREADCRUMBS_BASE, calculateOffset, POSTS_PER_PAGE } from 'utils/shared';
import PostList from 'components/PostList';
import { getPaginatedPosts } from 'lib/wordpress';
// import { BLOG_INDEX_REVLIDATION_TIME } from 'pages/page/[page]';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { BreadcrumbsItemType } from 'ui/src/components/Breadcrumbs';
import withLayoutHOF from 'components/withLayoutHOF';
import { STATIC_PAGINATION_THRESHOLD } from 'utils/addPageStaticPath';
import getPaginationHrefConstructor from 'utils/getPaginationHrefConstructor';
import { client } from 'lib/apollo/client';
import { AllPostsPathsDocument } from 'gql/graphql-operations';

export default function NotFound({ popularArticles, total }: InferGetStaticPropsType<typeof getStaticProps>) {
  const { query } = useRouter();
  const page = typeof query.page === 'string' && !Number.isNaN(Number(query.page)) ? Number(query.page) : 1;
  const subtitle = typeof query.value === 'string' ? query.value : '';
  const [searchValue, setSearchValue] = useState('');
  useEffect(() => {
    setSearchValue(subtitle);
  }, [subtitle]);
  const paginationHrefConstructor = getPaginationHrefConstructor({
    queryPageKey: 'page',
    href: { query: { page: query.page, value: searchValue }, pathname: '/search/not-found/page/[page]' },
  });

  const breadcrumbsItems: BreadcrumbsItemType[] = [
    ...BREADCRUMBS_BASE,
    {
      label: 'Search results',
    },
  ];

  return (
    <>
      <Head>
        <title>{BLOG_TITLE} | Not found</title>
      </Head>
      <PostList
        title={
          <>
            Sorry, no results for <span className="text-site-neutrals-500 break-words">{searchValue}</span>, but
            you might be interested on these popular articles:
          </>
        }
        posts={popularArticles}
        total={total}
        breadcrumbs={breadcrumbsItems}
        withCategories
        currentPage={page}
        paginationHrefConstructor={paginationHrefConstructor}
      />
    </>
  );
}

// const NOT_FOUND_REVALIDATION_TIME = BLOG_INDEX_REVLIDATION_TIME;

export const getStaticProps = withLayoutHOF(async ({ params }: GetStaticPropsContext) => {
  const { data, errors } = await getPaginatedPosts(calculateOffset(params?.page));

  if (errors) {
    throw new Error(`PAGINATED_POST_QUERY_OBJECT: ${JSON.stringify(errors)}`);
  }

  const popularArticles =
    data.posts?.edges?.map((post) => {
      if (!post?.node) return null;
      return { ...post.node, date: post.node.date ? new Date(post.node.date).toUTCString() : '' };
    }) || [];

  const total = data.posts?.pageInfo?.offsetPagination?.total ?? 0;
  return {
    props: { popularArticles, total },
    // revalidate: NOT_FOUND_REVALIDATION_TIME, // ISR
  };
});

export async function getStaticPaths() {
  if (!client) {
    throw new Error(`Internal Server Error. Error code: CLIENT_MISSING`);
  }
  const { data, errors } = await client.query({
    query: AllPostsPathsDocument,
  });

  if (errors) {
    throw new Error(`GET_TOTAL_POSTS_COUNT: ${JSON.stringify(errors)}`);
  }

  const totalPostsCount = data?.posts?.pageInfo?.offsetPagination?.total ?? 0;

  const totalPages = Math.ceil(totalPostsCount / POSTS_PER_PAGE);
  const staticGeneratedPages = Math.min(totalPages, STATIC_PAGINATION_THRESHOLD);

  const paths = new Array(staticGeneratedPages).fill(``).map((p, i) => ({
    params: {
      page: (i + 1).toString(),
    },
  }));

  return {
    paths: [...paths],
    fallback: 'blocking',
  };
}
